import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  LocalShipping as LocalShippingIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Fade } from '@mui/material';
import jwt_decode from 'jwt-decode';

const ShopOrderHistory = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openOrderDialog, setOpenOrderDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [currentUser, setCurrentUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);

  useEffect(() => {
    loadOrders();
    loadCurrentUser();
  }, []);

  const loadOrders = async () => {
      setLoading(true);
      try {
        const response = await api.get('/orders');
        
        // Controllo se response.data è un oggetto con una proprietà 'orders'
        const ordersArray = response.data.orders || response.data;
        
        // Ordina gli ordini per data di creazione (dal più recente)
        if (Array.isArray(ordersArray)) {
          setOrders(ordersArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        } else {
          console.error('Formato dati ordini non valido:', response.data);
          setOrders([]);
          setSnackbar({
            open: true,
            message: 'Formato dati ordini non valido. Contatta l\'amministratore.',
            severity: 'error'
          });
        }
      } catch (err) {
        console.error('Errore nel caricamento degli ordini:', err);
        setSnackbar({
          open: true,
          message: 'Errore nel caricamento degli ordini. Riprova più tardi.',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

  const loadCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      console.log('Dati utente:', userData); // Per debug
      
      setCurrentUser({
        id: userData.id,
        email: userData.email,
        role: userData.role
      });

      console.log('Ruolo utente:', userData.role); // Per debug
    } catch (err) {
      console.error('Errore nel caricamento dell\'utente:', err);
      setSnackbar({
        open: true,
        message: 'Errore nel caricamento delle informazioni utente',
        severity: 'error'
      });
    }
  };

  const handleOpenOrderDialog = (order) => {
    setSelectedOrder(order);
    setOpenOrderDialog(true);
  };

  const handleCloseOrderDialog = () => {
    setOpenOrderDialog(false);
    setSelectedOrder(null);
  };

  const handleOpenProductDialog = (product) => {
    setSelectedProduct(product);
    setOpenProductDialog(true);
  };

  const handleCloseProductDialog = () => {
    setSelectedProduct(null);
    setOpenProductDialog(false);
  };

  const handleAddToCart = async (item) => {
    try {
      // Verifica disponibilità prodotto
      const response = await api.get(`/products/${item.code}`);
      const product = response.data;

      if (!product) {
        setSnackbar({
          open: true,
          message: 'Prodotto non più disponibile nel listino',
          severity: 'error'
        });
        return;
      }

      if (product.quantity < item.quantity) {
        setSnackbar({
          open: true,
          message: `Quantità richiesta non disponibile. Disponibili: ${product.quantity} pezzi`,
          severity: 'warning'
        });
        return;
      }

      // Aggiungi al carrello
      const cartItem = {
        ...product,
        quantity: item.quantity,
        companyId: api.defaults.headers['x-company-id']
      };

      const currentCart = JSON.parse(localStorage.getItem(`shopCart_${api.defaults.headers['x-company-id']}`) || '[]');
      const existingItemIndex = currentCart.findIndex(i => i.code === item.code);

      if (existingItemIndex >= 0) {
        currentCart[existingItemIndex].quantity += item.quantity;
      } else {
        currentCart.push(cartItem);
      }

      localStorage.setItem(`shopCart_${api.defaults.headers['x-company-id']}`, JSON.stringify(currentCart));

      setSnackbar({
        open: true,
        message: 'Prodotto aggiunto al carrello',
        severity: 'success'
      });
    } catch (err) {
      console.error('Errore nell\'aggiunta al carrello:', err);
      setSnackbar({
        open: true,
        message: 'Errore nell\'aggiunta al carrello. Riprova più tardi.',
        severity: 'error'
      });
    }
  };

  const getItemTotal = (item) => {
    // Se il totale è già presente nell'item, lo usiamo
    if (item.total) {
      return parseFloat(item.total);
    }
    // Altrimenti lo calcoliamo dal prezzo e quantità
    return parseFloat(item.price) * parseFloat(item.quantity);
  };

  const getProductImage = (product) => {
    try {
      if (!product || !product.image) {
        return 'https://placehold.co/200x200?text=Prodotto';
      }

      // Se l'immagine è già un URL completo, lo usiamo direttamente
      if (typeof product.image === 'string' && (product.image.startsWith('http') || product.image.startsWith('data:'))) {
        return product.image;
      }

      // Se l'immagine è un oggetto, proviamo a estrarre l'URL
      if (typeof product.image === 'object') {
        const imageUrl = product.image.text || product.image.hyperlink || product.image.value || '';
        if (imageUrl && (imageUrl.startsWith('http') || imageUrl.startsWith('data:'))) {
          return imageUrl;
        }
      }

      // Se arriviamo qui, usiamo il placeholder
      return 'https://placehold.co/200x200?text=Prodotto';
    } catch (err) {
      console.error('Errore nel caricamento dell\'immagine:', err);
      return 'https://placehold.co/200x200?text=Prodotto';
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (date) => {
    return format(new Date(date), 'dd MMMM yyyy, HH:mm', { locale: it });
  };

  const handleDeleteClick = (order, event) => {
    event.stopPropagation(); // Previene l'apertura del dettaglio ordine
    console.log('Tentativo di eliminazione ordine da parte di:', currentUser); // Per debug
    if (!currentUser || currentUser.role !== 'superadmin') {
      setSnackbar({
        open: true,
        message: 'Non hai i permessi per eliminare gli ordini',
        severity: 'error'
      });
      return;
    }
    setOrderToDelete(order);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/orders/${orderToDelete.id}`);
      setDeleteConfirmOpen(false);
      setOrderToDelete(null);
      loadOrders(); // Ricarica la lista degli ordini
      setSnackbar({
        open: true,
        message: 'Ordine eliminato con successo',
        severity: 'success'
      });
    } catch (err) {
      console.error('Errore nell\'eliminazione dell\'ordine:', err);
      setSnackbar({
        open: true,
        message: 'Errore nell\'eliminazione dell\'ordine',
        severity: 'error'
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setOrderToDelete(null);
  };

  const checkProductAvailability = async (code) => {
    try {
      const searchResponse = await api.get(`/api/products?code=${code}`);
      return searchResponse.data.products.length > 0;
    } catch (error) {
      console.error('Errore nella verifica disponibilità prodotto:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
          Storico Ordini
        </Typography>

          {orders.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            Nessun ordine effettuato
            </Typography>
        </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Numero Ordine</TableCell>
                    <TableCell>Data</TableCell>
                <TableCell>Totale</TableCell>
                <TableCell>Stato</TableCell>
                {currentUser?.role === 'superadmin' && (
                    <TableCell align="center">Azioni</TableCell>
                )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  onClick={() => handleOpenOrderDialog(order)}
                  sx={{ cursor: 'pointer' }}
                >
                      <TableCell>{order.orderNumber}</TableCell>
                  <TableCell>
                    {new Date(order.createdAt).toLocaleDateString('it-IT')}
                  </TableCell>
                  <TableCell>€{parseFloat(order.total).toFixed(2)}</TableCell>
                  <TableCell>{order.status}</TableCell>
                  {currentUser?.role === 'superadmin' && (
                      <TableCell align="center">
                        <IconButton 
                        color="error"
                        onClick={(e) => handleDeleteClick(order, e)}
                          size="small" 
                      >
                        <DeleteIcon />
                        </IconButton>
                      </TableCell>
                  )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

      {/* Dialog dettaglio ordine */}
          <Dialog 
        open={openOrderDialog}
        onClose={handleCloseOrderDialog}
        TransitionComponent={Fade}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="h6">
                Dettaglio Ordine
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {selectedOrder && formatDate(selectedOrder.createdAt)}
              </Typography>
            </Grid>
            <Grid item>
              <Chip
                label={`Ordine #${selectedOrder?.orderNumber}`}
                color="primary"
                variant="outlined"
              />
            </Grid>
          </Grid>
            </DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Articoli Ordinati
                </Typography>
                <List>
                  {selectedOrder.items.map((item, index) => (
                    <React.Fragment key={index}>
                      <ListItem
                        button
                        onClick={() => handleOpenProductDialog(item)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                          <CardMedia
                            component="img"
                            image={getProductImage(item)}
                            alt={item.description}
                            sx={{ width: 60, height: 60, objectFit: 'contain', bgcolor: '#f5f5f5', borderRadius: 1 }}
                          />
                          <ListItemText
                            primary={item.description}
                            secondary={`Codice: ${item.code} - Quantità: ${item.quantity}`}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="primary">
                              €{getItemTotal(item).toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </ListItem>
                      {index < selectedOrder.items.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <LocalShippingIcon sx={{ mr: 1 }} color="primary" />
                        <Typography variant="subtitle1">
                          Indirizzo di Consegna
                        </Typography>
                    </Box>
                      <Typography variant="body2" paragraph>
                        {selectedOrder.shippingAddress?.name}<br />
                        {selectedOrder.shippingAddress?.address}<br />
                        {selectedOrder.shippingAddress?.postalCode} {selectedOrder.shippingAddress?.city} ({selectedOrder.shippingAddress?.province})
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ReceiptIcon sx={{ mr: 1 }} color="primary" />
                        <Typography variant="subtitle1">
                          Dati Fatturazione
                        </Typography>
                    </Box>
                      <Typography variant="body2">
                        {selectedOrder.billingAddress?.name}<br />
                        {selectedOrder.billingAddress?.address}<br />
                        {selectedOrder.billingAddress?.postalCode} {selectedOrder.billingAddress?.city} ({selectedOrder.billingAddress?.province})<br />
                        P.IVA: {selectedOrder.billingAddress?.vatNumber}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Riepilogo Importi
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        Imponibile:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2">
                        €{parseFloat(selectedOrder.subtotal).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        IVA (22%):
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2">
                        €{parseFloat(selectedOrder.vat).toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle1">
                        Totale:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography variant="subtitle1" color="primary">
                        €{parseFloat(selectedOrder.total).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrderDialog}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog dettaglio prodotto */}
      <Dialog
        open={openProductDialog}
        onClose={handleCloseProductDialog}
        TransitionComponent={Fade}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="subtitle2" color="textSecondary">
            Codice: {selectedProduct?.code}
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {selectedProduct?.description}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProduct && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <CardMedia
                  component="img"
                  image={getProductImage(selectedProduct)}
                  alt={selectedProduct.description}
                  sx={{ height: 300, objectFit: 'contain', bgcolor: '#f5f5f5', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Quantità ordinata
                        </Typography>
                        <Typography variant="body1">
                          {selectedProduct.quantity}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Prezzo unitario
                        </Typography>
                        <Typography variant="body1">
                          €{(getItemTotal(selectedProduct) / selectedProduct.quantity).toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Totale
                        </Typography>
                        <Typography variant="h6" color="primary">
                          €{getItemTotal(selectedProduct).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseProductDialog}>
                Chiudi
              </Button>
            </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          Sei sicuro di voler eliminare l'ordine {orderToDelete?.orderNumber}?
          Questa azione non può essere annullata.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Annulla</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Elimina
              </Button>
            </DialogActions>
          </Dialog>

      {/* Snackbar per le notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShopOrderHistory;